import { mapGetters } from "vuex";
import { formatPrice, rowHasVisibleDiscount } from "@/helpers";

/**
 * Cart money under the "1+1=3" promo, shared by the cart page, the mini-cart
 * popup and the checkout page.
 *
 * `state.basket` says which lines exist and how many units of each — it is
 * local, instant and persisted. `order/saleRows` says what those lines cost —
 * it comes from `GET /v1/products/check-sale`, which the cart already calls on
 * every change alongside `check-gift` / `check-samples`.
 *
 * Every method here answers the same question: do we have the backend's number
 * for this row, and what do we show when we don't?
 */
export default {
  computed: {
    ...mapGetters({
      basket: "basket/basket",
      saleRows: "order/saleRows",
      salePromo: "order/salePromo",
      saleRaffle: "order/saleRaffle",
      selectedBonuses: "basket/selectedBonuses",
    }),
    /**
     * Spending bonuses renders the cart at `basePrice` and voids every discount.
     * One definition instead of the three that used to live in the cart page,
     * the checkout page and the popup.
     */
    priceType() {
      return this.saleBonusesAmount > 0 ? "basePrice" : "price";
    },
    saleBonusesAmount() {
      // The cart and checkout pages own a local `bonusesAmount` field; the popup
      // has none and its copy of `priceType` silently read `undefined`.
      if (typeof this.bonusesAmount === "number") return this.bonusesAmount;

      return (this.selectedBonuses && this.selectedBonuses.pay_bonus_amount) || 0;
    },
    /**
     * The promo counters, or null while bonuses are being spent — see `saleRow`.
     * `promo.discount` is the backend's own `Σ (old_sum − sum)`, so it can come
     * out lower than the free unit is worth when the promo cancelled a
     * percentage discount elsewhere in the cart.
     */
    promo() {
      return this.saleBonusesAmount > 0 ? null : this.salePromo;
    },
    /** Cart total. Never a sum of `price × count` — under 1+1=3 they differ. */
    cartTotal() {
      return (this.basket || []).reduce((sum, product) => sum + this.rowSum(product), 0);
    },
  },
  methods: {
    formatPrice,
    /**
     * The backend row for a cart line, or null to fall back to local prices.
     *
     * Null in bonuses mode: the promo does not apply there, and rendering the
     * backend's promo price next to a bonuses-mode total would show the customer
     * two contradicting numbers.
     */
    saleRow(basketProduct) {
      if (this.saleBonusesAmount > 0) return null;
      if (!basketProduct || !basketProduct.select_type) return null;

      return (this.saleRows && this.saleRows[basketProduct.select_type.id]) || null;
    },
    /**
     * The backend priced this exact quantity. Anything else is a leftover from
     * before the last change — check-sale answers in well under a second, so the
     * fallback below is a blink rather than a state worth drawing a skeleton for.
     */
    isRowFresh(basketProduct) {
      const row = this.saleRow(basketProduct);

      return !!row && row.count === basketProduct.select_count;
    },
    freshRow(basketProduct) {
      return this.isRowFresh(basketProduct) ? this.saleRow(basketProduct) : null;
    },
    /** The row total. */
    rowSum(basketProduct) {
      const row = this.freshRow(basketProduct);
      if (row) return row.sum;

      return basketProduct.select_type[this.priceType] * basketProduct.select_count;
    },
    rowUnitPrice(basketProduct) {
      const row = this.freshRow(basketProduct);
      if (row) return row.unitPrice;

      return basketProduct.select_type[this.priceType];
    },
    /**
     * The struck-through row total, or null for no strikethrough. Always a
     * backend figure, never one derived here.
     *
     * 1. A row the promo touched carries `oldSum` — but it is struck only when
     *    it is genuinely higher than `sum`. **The promo can make a row dearer:**
     *    it strips the percentage discount from every row it touches while
     *    handing the free unit to only one of them, so a discounted row that
     *    missed out reports `oldSum: 5646` against `sum: 6642`. Striking that
     *    would advertise a saving that is really a surcharge.
     * 2. Everything else keeps the ordinary `oldPrice`, and only when it is
     *    real — the «−0%» campaigns send `oldPrice === price`.
     */
    rowOldSum(basketProduct) {
      const row = this.freshRow(basketProduct);
      if (row) {
        if (row.oldSum != null && row.oldSum > row.sum) return row.oldSum;

        return rowHasVisibleDiscount(row) ? row.oldPrice * row.count : null;
      }

      const price = basketProduct.select_type[this.priceType];
      if (price === basketProduct.select_type.basePrice) return null;

      return basketProduct.select_type.basePrice * basketProduct.select_count;
    },
    /** Same rule for the per-unit column. */
    rowOldUnitPrice(basketProduct) {
      const row = this.freshRow(basketProduct);
      if (row) return rowHasVisibleDiscount(row) ? row.oldPrice : null;

      const price = basketProduct.select_type[this.priceType];

      return price === basketProduct.select_type.basePrice ? null : basketProduct.select_type.basePrice;
    },
  },
};
